import { useMemo } from 'react'
import { useClub } from '../club/ClubProvider.tsx'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { isOpen } from '../sync/projectors.ts'
import { rangeBounds, type AssetRow } from './fleet.ts'
import type { JobRow } from './plan.ts'
import { pickChecklist, type ChecklistVersionRow, type WorkOrderRow } from './safety.ts'
import { localDate } from '../lib/time.ts'

/** Doing the work (ARCHITECTURE 7.7, 8.5; M8), as the device sees it: the server's rows with this device's pending facts laid over them, so a timer started with no signal
 *  is running on screen at once. Start, pause, resume and finish are FACTS written offline; the server's own rows replace the projections when they arrive. */
export interface WorkSessionRow {
  id: string; person_id: string; job_id: string | null; work_order_id: string | null; recorded_by: string; asset_id: string | null
  template_version_id: string | null; procedure_version_id: string | null; started_at: string; closed_at: string | null
}
export interface WorkIntervalRow {
  id: string; person_id: string; work_session_id: string; during: string; source: 'timer' | 'manual' | 'estimated' | 'correction'; review_state: 'none' | 'pending' | 'accepted' | 'rejected'
  corrects_interval_id: string | null; superseded_by_id: string | null; exception_code: string | null; note: string | null; created_at?: string
  /** the device that opened it (the server's provenance column; this device's own id on the projection of its pending start or resume) */
  device_id?: string | null
}
export interface JobCompletionRow { id: string; person_id: string; job_id: string; work_session_id: string; outcome: 'complete' | 'partial'; completed_at: string; notes: string | null; remaining_job_id: string | null }
export interface CoverageRow { id: string; person_id: string; completion_id: string; area_id: string; area_version_id: string | null; coverage: 'full' | 'partial' }

export const useWorkSessions = () => useMerged<WorkSessionRow>('work_session', 'ops.work_session')
export const useWorkIntervals = () => useMerged<WorkIntervalRow>('work_interval', 'ops.work_interval')
export const useCompletions = () => useMerged<JobCompletionRow>('job_completion', 'ops.job_completion')
export const useCoverage = () => useMerged<CoverageRow>('job_area_coverage', 'ops.job_area_coverage')

/** `["2026-10-17 05:18:00+00","2026-10-17 06:02:00+00")` -> instants (an open range has no end). */
export const intervalBounds = (during: string): { from: Date | null; to: Date | null } => rangeBounds(during)
export const msBetween = (from: Date | null, to: Date | null, now: number): number => (from ? Math.max(0, (to?.getTime() ?? now) - from.getTime()) : 0)
/** The time worked in one session, in ms, from its intervals that still count (a corrected one is replaced by its correction; a rejected one counts for nothing). */
export function sessionMs(intervals: readonly WorkIntervalRow[], now: number): number {
  return intervals.filter((i) => !i.superseded_by_id && i.review_state !== 'rejected').reduce((n, i) => { const b = intervalBounds(i.during); return n + msBetween(b.from, b.to, now) }, 0)
}
export const formatDuration = (ms: number): string => {
  const m = Math.floor(ms / 60_000)
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`
}
export const formatClock = (ms: number): string => {
  const s = Math.floor(ms / 1000)
  return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

export interface RunningWork<J extends Pending<JobRow> = Pending<JobRow>> {
  session: Pending<WorkSessionRow>
  job: J | null
  /** the interval being timed now; null while paused */
  running: Pending<WorkIntervalRow> | null
  intervals: Array<Pending<WorkIntervalRow>>
}

/** The person's open sessions with their timers. On a shared workstation: the unlocked person's. */
export function useMyWork<J extends Pending<JobRow>>(jobs: J[] | undefined): { ready: boolean; mine: Array<RunningWork<J>>; person: string | null } {
  const { personId, kiosk } = useClub()
  const me = kiosk?.session?.personId ?? personId
  const sessions = useWorkSessions(); const intervals = useWorkIntervals()
  return useMemo(() => {
    const ready = !!sessions && !!intervals && !!jobs
    if (!ready || !me) return { ready, mine: [], person: me }
    const byJob = new Map(jobs!.map((j) => [j.id, j]))
    const mine = sessions!.filter((s) => s.person_id === me && !s.closed_at).map((s) => {
      const own = intervals!.filter((i) => i.work_session_id === s.id)
      return { session: s, job: s.job_id ? byJob.get(s.job_id) ?? null : null, running: own.find((i) => isOpen(i) && !i.superseded_by_id) ?? null, intervals: own }
    }).sort((a, b) => a.session.started_at.localeCompare(b.session.started_at))
    return { ready, mine, person: me }
  }, [sessions, intervals, jobs, me])
}

/** A repair order as a timer names it (M23 item 44: a session on an order, no job): "Réparation · TR-01 · GM-3000 · Fuite hydraulique". Pure; the
 *  words come from the catalogue through `t`. */
export const orderLabel = (o: Pick<WorkOrderRow, 'kind' | 'asset_id' | 'complaint'>, assets: ReadonlyArray<Pick<AssetRow, 'id' | 'readable_tag' | 'model'>>, t: (k: string) => string, max = 40): string => {
  const asset = assets.find((a) => a.id === o.asset_id)
  const complaint = (o.complaint ?? '').trim()
  return [t(`workorder.kind.${o.kind}`), asset ? asset.readable_tag + (asset.model ? ` · ${asset.model}` : '') : '', complaint ? (complaint.length > max ? `${complaint.slice(0, max - 1)}…` : complaint) : ''].filter(Boolean).join(' · ')
}
/** What a session is called on a card: the job's title, else the order it was opened on, else plain workshop labour. */
export function sessionTitle(session: Pick<WorkSessionRow, 'job_id' | 'work_order_id'>, jobTitle: string | undefined, orders: ReadonlyArray<Pick<WorkOrderRow, 'id' | 'kind' | 'asset_id' | 'complaint'>> | undefined, assets: ReadonlyArray<Pick<AssetRow, 'id' | 'readable_tag' | 'model'>> | undefined, t: (k: string) => string): string {
  if (session.job_id && jobTitle) return jobTitle
  const order = session.work_order_id ? orders?.find((o) => o.id === session.work_order_id) : undefined
  return order ? `${t('work.workshopLabour')} · ${orderLabel(order, assets ?? [], t)}` : t('work.workshopLabour')
}

export interface InspectionLite { id: string; asset_id: string; outcome: string; submitted_at: string }
export const useInspections = () => useMerged<InspectionLite>('inspection', 'fleet.inspection')
/** Is the machine's pre-start check done today (site time)? Only informs the screen: the server flags a start without one and the person is never stopped by it
 *  (a failed critical check restricts the machine, which is its own, stronger rule). */
export function prestartState(asset: Pick<AssetRow, 'asset_type_code'> | undefined, versions: ChecklistVersionRow[] | undefined, inspections: InspectionLite[] | undefined, assetId: string, timezone: string, now = new Date()): { state: 'unknown' | 'not_required' | 'done' | 'missing'; at?: string } {
  if (!asset || !versions || !inspections) return { state: 'unknown' }
  if (!pickChecklist(asset, versions)) return { state: 'not_required' }
  const today = localDate(now, timezone)
  const done = inspections.filter((i) => i.asset_id === assetId && i.outcome !== 'fail_critical' && localDate(i.submitted_at, timezone) === today).sort((a, b) => (a.submitted_at < b.submitted_at ? 1 : -1))[0]
  return done ? { state: 'done', at: done.submitted_at } : { state: 'missing' }
}
